/**
 * A failed request, shown with whatever the server said would clear it.
 *
 * The API returns real explanations — which role an action needs, which checks
 * did not pass, which findings block issue. Swallowing those and printing
 * "Something went wrong" would throw away the only useful part of the failure.
 */
import { ApiError } from '../lib/api';
import { FindingList } from './FindingList';

export interface ErrorStateProps {
  error: unknown;
  /** Re-runs the request. */
  onRetry?: () => void;
  /** Overrides the heading, e.g. "The quote could not be issued". */
  title?: string;
  compact?: boolean;
  className?: string;
}

export function ErrorState({ error, onRetry, title, compact = false, className }: ErrorStateProps) {
  const apiError = error instanceof ApiError ? error : null;
  const message =
    apiError?.message ?? (error instanceof Error ? error.message : String(error ?? 'Unknown error'));

  const heading =
    title ??
    (apiError?.isForbidden
      ? 'Your role may not do that'
      : apiError?.isNotFound
        ? 'Not found'
        : apiError?.isConflict
          ? 'Something moved underneath this'
          : apiError?.status === 0
            ? 'The API did not respond'
            : 'The request failed');

  return (
    <div
      className={['error-state', compact ? 'error-compact' : '', className].filter(Boolean).join(' ')}
      role="alert"
    >
      <div className="error-head">
        <strong>{heading}</strong>
        {apiError && apiError.status > 0 && <code className="error-status">HTTP {apiError.status}</code>}
      </div>
      <p className="error-message">{message}</p>

      {apiError && apiError.checks.length > 0 && (
        <ul className="error-checks">
          {apiError.checks.map((check) => (
            <li key={check.key} className={check.passed ? 'check-passed' : 'check-failed'}>
              <strong>{check.label}</strong>
              <span>{check.detail}</span>
            </li>
          ))}
        </ul>
      )}

      {apiError && apiError.findings.length > 0 && <FindingList findings={apiError.findings} />}

      {apiError?.status === 0 && (
        <p className="error-hint">
          Start it with <code>npm run dev -w @pts/api</code> from the repository root, then retry.
        </p>
      )}

      {onRetry && (
        <div className="error-action">
          <button type="button" className="secondary" onClick={onRetry}>
            Try again
          </button>
        </div>
      )}

      {apiError?.requestId && <p className="error-request-id">Request {apiError.requestId}</p>}
    </div>
  );
}

export default ErrorState;
